import { Fragment, useState } from 'react';
import { useResearch } from './ResearchTabs.jsx';

const domainOf = (url) => {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return ''; }
};

const options = (items) => items.map((item) => <option key={item.key} value={item.key}>{item.name}</option>);

const money = (value) => {
  if (value < 1) return `${Math.round(value * 100)}p`;
  if (value < 10) return `£${value.toFixed(2).replace(/\.00$/, '')}`;
  return `£${Math.round(value).toLocaleString('en-GB')}`;
};

const columns = [['month', 'Per month'], ['year', 'Per year'], ['three', 'Every 3 years'], ['five', 'Every 5 years'], ['longer', 'Longer']];

/** The column a row belongs in: how often it is really paid. A row with no pound figure belongs in none. */
function columnOf(row) {
  if (row.column) return row.column;
  if (!row.times) return null;
  const fewest = row.times[0];
  if (fewest >= 6) return 'month';
  if (fewest >= 1) return 'year';
  if (1 / fewest <= 3.01) return 'three';
  return 1 / fewest <= 5.01 ? 'five' : 'longer';
}

/** A row's low and high cost in its column: a month's or a year's worth when it is paid that often, otherwise the price each time. */
function amount(row) {
  const [low, high = low] = row.cost;
  const column = columnOf(row);
  if (column !== 'month' && column !== 'year') return [low, high];
  const [fewest, most = fewest] = row.times;
  const share = column === 'month' ? 1 / 12 : 1;
  return [low * fewest * share, high * most * share];
}

function cost(row) {
  if (row.costText) return row.costText;
  const [from, to] = amount(row).map(money);
  const figure = `${row.open === 'from' ? 'From ' : ''}${from === to ? from : `${from}–${to}`}${row.open === 'plus' ? '+' : ''}`;
  return <><span className="revenue-cost">{figure}</span>{row.per ? ` ${row.per}` : ''}</>;
}

/** The middle of what `rows` add up to in a column, rounded to keep the foot of the sheet easy to read. */
function middle(rows, column) {
  const [low, high] = rows.filter((row) => row.cost && columnOf(row) === column).map(amount)
    .reduce(([a, b], [rowLow, rowHigh]) => [a + rowLow, b + rowHigh], [0, 0]);
  const value = (low + high) / 2;
  return value < 100 ? Math.round(value) : Math.round(value / 10) * 10;
}

const pounds = (value) => (value ? `£${value.toLocaleString('en-GB')}` : '–');

/**
 * The Revenue tab: the money third parties make from housing providers, by BedMaps module (Repairs, Compliance, Utilities & Bills,
 * Finance…). One table lists everyone who gets paid: what they sell, the typical UK price and how often it is paid, with the cost once,
 * unspread, in the column for how often that is. The Module dropdown (`module`) and the Who gets paid dropdown (`payee`, such as
 * plumbers or insurers) narrow it. The foot adds up each column, takes away what we can get cheaper quotes for and leaves the prices
 * nobody can shop around (`fixed`). Show sources adds where each price came from. `onOpen(params)` changes what is shown.
 * The research is read from the database when the tab opens, and only admins can read it (supabase/research/revenue.json is the source).
 */
export default function RevenueTab({ module, payee, onOpen }) {
  const [sources, setSources] = useState(false);
  const { rows: catalogue, error } = useResearch('revenue');
  if (error) return <p className="mod-error" role="alert">{error}</p>;
  if (!catalogue) return <p>Loading revenue…</p>;
  if (catalogue.length === 0) return <p className="admin-empty">Nothing on Revenue yet.</p>;
  const open = catalogue.find((item) => item.key === module);
  const modules = open ? [open] : catalogue;
  const who = modules.flatMap((item) => item.payees).find((item) => item.key === payee);
  const payees = modules.flatMap((item) => item.payees).filter((item) => !who || item === who);
  const itemsOf = (list) => list.flatMap((item) => item.items);
  const quotable = itemsOf(payees.filter((item) => !item.fixed));
  const fixed = itemsOf(payees.filter((item) => item.fixed));
  const choose = (moduleKey, payeeKey) => onOpen({ ...(moduleKey && { module: moduleKey }), ...(payeeKey && { payee: payeeKey }) });
  return (
    <>
      <div className="mod-filters">
        <label>Module
          <select value={open?.key || ''} onChange={(event) => choose(event.target.value)}>
            <option value="">All modules</option>
            {options(catalogue)}
          </select>
        </label>
        {(!open || open.payees.length > 1) && (
          <label>Who gets paid
            <select value={who?.key || ''} onChange={(event) => choose(open?.key, event.target.value)}>
              <option value="">Everyone</option>
              {open ? options(open.payees) : catalogue.map((item) => <optgroup key={item.key} label={item.name}>{options(item.payees)}</optgroup>)}
            </select>
          </label>
        )}
        <label className="mod-check revenue-sources"><input type="checkbox" checked={sources} onChange={(event) => setSources(event.target.checked)}/> Show sources</label>
      </div>
      <div className="mod-table-wrap">
        {/* One row per thing sold; a business's name spans its rows. With no module chosen, each module has a row above its businesses. */}
        <table className="mod-audit data-catalogue data-records" aria-label="What housing providers pay third parties for">
          <thead><tr><th scope="col">Who gets paid</th><th scope="col">What they sell</th><th scope="col">Typical price</th><th scope="col">How often</th>{columns.map(([key, label]) => <th scope="col" className="revenue-cost" key={key}>{label}</th>)}{sources && <th scope="col">Source</th>}</tr></thead>
          {modules.filter((item) => !who || item.payees.includes(who)).map((item) => (
            <Fragment key={item.key}>
              {!open && <tbody className="revenue-module"><tr><th scope="rowgroup" colSpan={4 + columns.length + (sources ? 1 : 0)}>{item.name}</th></tr></tbody>}
              {item.payees.filter((entry) => !who || entry === who).map((entry) => (
                <tbody key={entry.key}>
                  {entry.items.map((row, index) => (
                    <tr key={row.what}>
                      {index === 0 && <th scope="rowgroup" rowSpan={entry.items.length}>{entry.name}</th>}
                      <td className="data-point">{row.what}</td>
                      <td className="data-example">{row.price ? `${row.price}${row.basis ? ` ${row.basis}` : ''}` : 'Not found yet'}</td>
                      <td className="data-example">{row.often || '–'}{row.assumed ? ` (assumed ${row.assumed})` : ''}</td>
                      {columns.map(([key]) => <td key={key}>{columnOf(row) === key ? cost(row) : ''}</td>)}
                      {sources && (
                        <td className="data-example">
                          {row.source ? <a className="admin-ext" href={row.source} target="_blank" rel="noreferrer">{domainOf(row.source) || 'Source'}</a> : '–'}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              ))}
            </Fragment>
          ))}
          <tfoot className="revenue-total">
            {[['Total', (key) => pounds(middle(quotable, key) + middle(fixed, key))],
              ['Less what we can get cheaper quotes for', (key) => (middle(quotable, key) ? `−${pounds(middle(quotable, key))}` : '–')],
              ['Left after that', (key) => pounds(middle(fixed, key))]].map(([label, figure]) => (
              <tr key={label}>
                <th scope="row" colSpan={4}>{label}</th>
                {columns.map(([key]) => <td key={key}>{figure(key)}</td>)}
                {sources && <td/>}
              </tr>
            ))}
          </tfoot>
        </table>
      </div>
    </>
  );
}
